import type { StorageEntityValue } from '../../types/azureStorage';

export type AzureStorageODataOperator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le';
export type AzureStorageODataAst =
  | { kind: 'comparison'; property: string; operator: AzureStorageODataOperator; value: StorageEntityValue }
  | { kind: 'and' | 'or'; left: AzureStorageODataAst; right: AzureStorageODataAst }
  | { kind: 'not'; operand: AzureStorageODataAst };

type Token = { kind: 'word' | 'string' | 'number' | 'typed' | 'lparen' | 'rparen'; text: string; at: number; prefix?: string };
export type AzureStorageODataValidationError = { code: 'InvalidFilter' | 'TooManyComparisons' | 'TypeMismatch'; message: string };
export type AzureStorageODataValidation = { ok: true; ast: AzureStorageODataAst; comparisons: number } | { ok: false; error: AzureStorageODataValidationError };

const identifier = /^[A-Za-z_][A-Za-z0-9_]*$/;
/**
 * Special Doubles (real service, probed 2026-09-24): bare upper-case `INF` / `-INF` work with every operator and a
 * stored NaN sorts above INF; `NaN` follows IEEE equality (`X eq NaN` matches nothing, not even a stored NaN) and
 * every other spelling is a 400 / 501. So INF / -INF are literals here and NaN is refused with this message.
 */
export const AZURE_TABLE_NAN_FILTER_MESSAGE = 'Azure Table cannot match NaN with a comparison (X eq NaN matches nothing); NaN sorts above INF, so use gt INF to find NaN values.';
const operators = new Set<AzureStorageODataOperator>(['eq', 'ne', 'gt', 'ge', 'lt', 'le']);
const typedPrefixes = new Set(['datetime', 'guid', 'binary']);
const INT64_MIN = -(1n << 63n);
const INT64_MAX = (1n << 63n) - 1n;
const hexToBase64 = (hex: string): string => {
  let binary = '';
  for (let index = 0; index < hex.length; index += 2) binary += String.fromCharCode(Number.parseInt(hex.slice(index, index + 2), 16));
  return btoa(binary);
};
const base64ToHex = (base64: string): string => {
  const binary = atob(base64);
  return [...binary].map((character) => character.charCodeAt(0).toString(16).padStart(2, '0')).join('');
};

function quoted(source: string, start: number): { value: string; end: number } {
  let value = '';
  for (let i = start + 1; i < source.length; i += 1) {
    if (source[i] !== "'") { value += source[i]; continue; }
    if (source[i + 1] === "'") { value += "'"; i += 1; continue; }
    return { value, end: i + 1 };
  }
  throw new Error(`Unterminated string literal at ${start}.`);
}

function tokenize(source: string): Token[] {
  const out: Token[] = [];
  for (let i = 0; i < source.length;) {
    if (/\s/.test(source[i])) { i += 1; continue; }
    if (source[i] === '(' || source[i] === ')') { out.push({ kind: source[i] === '(' ? 'lparen' : 'rparen', text: source[i], at: i }); i += 1; continue; }
    if (source[i] === "'") { const q = quoted(source, i); out.push({ kind: 'string', text: q.value, at: i }); i = q.end; continue; }
    if (/^-INF(?![A-Za-z0-9_])/.test(source.slice(i))) { out.push({ kind: 'word', text: '-INF', at: i }); i += 4; continue; }
    const number = source.slice(i).match(/^-?(?:\d+\.\d*|\d*\.\d+|\d+)(?:[eE][+-]?\d+)?L?/);
    if (number) { out.push({ kind: 'number', text: number[0], at: i }); i += number[0].length; continue; }
    const word = source.slice(i).match(/^[A-Za-z_][A-Za-z0-9_]*/);
    if (!word) throw new Error(`Unexpected character at ${i}.`);
    const prefix = word[0].toLowerCase();
    const next = i + word[0].length;
    if (typedPrefixes.has(prefix) && source[next] === "'") {
      const q = quoted(source, next); out.push({ kind: 'typed', text: q.value, prefix, at: i }); i = q.end; continue;
    }
    out.push({ kind: 'word', text: word[0], at: i }); i = next;
  }
  return out;
}

function literal(token: Token | undefined): StorageEntityValue {
  if (!token) throw new Error('Expected a literal at end of filter.');
  if (token.kind === 'string') return { t: 'string', v: token.text };
  if (token.kind === 'number') {
    if (token.text.endsWith('L')) {
      const text = token.text.slice(0, -1);
      const value = BigInt(text);
      if (value < INT64_MIN || value > INT64_MAX) throw new Error(`Int64 literal at ${token.at} is outside the signed 64-bit range.`);
      return { t: 'int64', v: text };
    }
    if (/[.eE]/.test(token.text)) return { t: 'double', v: Number(token.text) };
    const value = Number(token.text);
    if (!Number.isSafeInteger(value) || value < -2147483648 || value > 2147483647) throw new Error(`Integer literal at ${token.at} needs the Int64 L suffix.`);
    return { t: 'int32', v: value };
  }
  if (token.kind === 'typed') {
    if (token.prefix === 'datetime') { if (!Number.isFinite(Date.parse(token.text))) throw new Error('Invalid datetime literal.'); return { t: 'datetime', v: token.text }; }
    if (token.prefix === 'guid') { if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token.text)) throw new Error('Invalid guid literal.'); return { t: 'guid', v: token.text }; }
    if (!/^(?:[0-9a-f]{2})*$/i.test(token.text)) throw new Error('Binary literals require an even number of hexadecimal digits.');
    return { t: 'binary', v: hexToBase64(token.text) };
  }
  if (token.kind === 'word' && /^(true|false)$/i.test(token.text)) return { t: 'boolean', v: token.text.toLowerCase() === 'true' };
  if (token.kind === 'word' && (token.text === 'INF' || token.text === '-INF')) return { t: 'double', v: token.text === 'INF' ? 'Infinity' : '-Infinity' };
  if (token.kind === 'word' && token.text === 'NaN') throw new Error(AZURE_TABLE_NAN_FILTER_MESSAGE);
  throw new Error(`Expected a typed literal at ${token.at}.`);
}

export function parseAzureStorageOData(source: string): AzureStorageODataAst {
  const tokens = tokenize(source); let position = 0;
  const peek = () => tokens[position];
  const takeWord = (word: string) => peek()?.kind === 'word' && peek().text.toLowerCase() === word ? (position += 1, true) : false;
  const primary = (): AzureStorageODataAst => {
    if (peek()?.kind === 'lparen') { position += 1; const node = or(); if (peek()?.kind !== 'rparen') throw new Error('Expected closing parenthesis.'); position += 1; return node; }
    const property = tokens[position++]; const op = tokens[position++]; const value = tokens[position++];
    if (property?.kind !== 'word' || !identifier.test(property.text)) throw new Error(`Expected a property identifier at ${property?.at ?? source.length}.`);
    const normalized = op?.kind === 'word' ? op.text.toLowerCase() as AzureStorageODataOperator : undefined;
    if (!normalized || !operators.has(normalized)) throw new Error(`Expected a comparison operator at ${op?.at ?? source.length}.`);
    return { kind: 'comparison', property: property.text, operator: normalized, value: literal(value) };
  };
  const not = (): AzureStorageODataAst => takeWord('not') ? { kind: 'not', operand: not() } : primary();
  const and = (): AzureStorageODataAst => { let node = not(); while (takeWord('and')) node = { kind: 'and', left: node, right: not() }; return node; };
  const or = (): AzureStorageODataAst => { let node = and(); while (takeWord('or')) node = { kind: 'or', left: node, right: and() }; return node; };
  if (tokens.length === 0) throw new Error('Filter is empty.');
  const ast = or(); if (position !== tokens.length) throw new Error(`Unexpected token at ${tokens[position].at}.`); return ast;
}

export const countAzureStorageODataComparisons = (ast: AzureStorageODataAst): number => ast.kind === 'comparison' ? 1 : ast.kind === 'not' ? countAzureStorageODataComparisons(ast.operand) : countAzureStorageODataComparisons(ast.left) + countAzureStorageODataComparisons(ast.right);
const quote = (value: string) => `'${value.replace(/'/g, "''")}'`;
/**
 * A Double is emitted as a plain decimal with a decimal point (S4 review C,
 * M8): the point keeps it from reading back as Int32, and the exponent of
 * `String(v)` is expanded — `1e21` used to become the invalid `1e+21.0`, and
 * Azurite silently matches nothing for any exponent literal (probed: `1e+21`,
 * `1E+21`, `1.0e+21`, `1e-7` all return 0 rows; `1000000000000000000000.0` and
 * `0.0000001` match). The shortest round-trip digits are kept, so the
 * expansion parses back to the identical double.
 */
const plainDecimal = (value: number): string => {
  const text = String(value);
  const match = /^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/.exec(text);
  if (!match) return text;
  const [, sign, whole, fraction = '', exponent] = match;
  const digits = whole + fraction;
  const point = whole.length + Number(exponent);
  if (point <= 0) return `${sign}0.${'0'.repeat(-point)}${digits}`;
  if (point >= digits.length) return `${sign}${digits}${'0'.repeat(point - digits.length)}`;
  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
};
const formatDouble = (value: number): string => {
  if (value === Infinity || value === -Infinity) return value > 0 ? 'INF' : '-INF';
  if (!Number.isFinite(value)) return String(value);
  const text = plainDecimal(value);
  return text.includes('.') ? text : `${text}.0`;
};
const formatLiteral = (value: StorageEntityValue): string => value.t === 'string' ? quote(value.v) : value.t === 'int64' ? `${value.v}L` : value.t === 'double' ? formatDouble(Number(value.v)) : value.t === 'binary' ? `binary${quote(base64ToHex(value.v))}` : value.t === 'datetime' || value.t === 'guid' ? `${value.t}${quote(value.v)}` : String(value.v);
export const formatAzureStorageOData = (ast: AzureStorageODataAst): string => ast.kind === 'comparison' ? `${ast.property} ${ast.operator} ${formatLiteral(ast.value)}` : ast.kind === 'not' ? `not (${formatAzureStorageOData(ast.operand)})` : `${ast.left.kind === 'comparison' ? formatAzureStorageOData(ast.left) : `(${formatAzureStorageOData(ast.left)})`} ${ast.kind} ${ast.right.kind === 'comparison' ? formatAzureStorageOData(ast.right) : `(${formatAzureStorageOData(ast.right)})`}`;

// A special Double (`'NaN'` / `'Infinity'` / `'-Infinity'` transport text, M9) compares as its number.
const comparable = (value: StorageEntityValue): string | number | bigint | boolean => value.t === 'int64' ? BigInt(value.v) : value.t === 'datetime' ? Date.parse(value.v) : value.t === 'double' ? Number(value.v) : value.v;
function compare(actual: StorageEntityValue | undefined, expected: StorageEntityValue, op: AzureStorageODataOperator): boolean {
  if (!actual) return false;
  const numeric = new Set(['int32', 'int64', 'double']);
  if (actual.t !== expected.t && !(numeric.has(actual.t) && numeric.has(expected.t))) return false;
  const left = comparable(actual); const right = comparable(expected);
  // The service orders a stored NaN above INF and never equal to anything (probed 2026-09-24).
  if (typeof left === 'number' && Number.isNaN(left)) return op === 'gt' || op === 'ge' || op === 'ne';
  if (typeof left !== typeof right && (typeof left === 'bigint' || typeof right === 'bigint')) {
    const numberValue = typeof left === 'number' ? left : right as number;
    if (!Number.isInteger(numberValue)) {
      if (op === 'eq') return false;
      if (op === 'ne') return true;
      return op === 'gt' ? left > right : op === 'ge' ? left >= right : op === 'lt' ? left < right : left <= right;
    }
    const l = BigInt(left); const r = BigInt(right); return op === 'eq' ? l === r : op === 'ne' ? l !== r : op === 'gt' ? l > r : op === 'ge' ? l >= r : op === 'lt' ? l < r : l <= r;
  }
  return op === 'eq' ? left === right : op === 'ne' ? left !== right : op === 'gt' ? left > right : op === 'ge' ? left >= right : op === 'lt' ? left < right : left <= right;
}
export const evaluateAzureStorageOData = (ast: AzureStorageODataAst, properties: Record<string, StorageEntityValue | undefined>): boolean => ast.kind === 'comparison' ? compare(properties[ast.property], ast.value, ast.operator) : ast.kind === 'not' ? !evaluateAzureStorageOData(ast.operand, properties) : ast.kind === 'and' ? evaluateAzureStorageOData(ast.left, properties) && evaluateAzureStorageOData(ast.right, properties) : evaluateAzureStorageOData(ast.left, properties) || evaluateAzureStorageOData(ast.right, properties);

export function validateAzureStorageOData(source: string, options: { declaredTypes?: Record<string, StorageEntityValue['t']> } = {}): AzureStorageODataValidation {
  try {
    const ast = parseAzureStorageOData(source); const comparisons = countAzureStorageODataComparisons(ast);
    if (comparisons > 15) return { ok: false, error: { code: 'TooManyComparisons', message: 'Azure Table filters support at most 15 comparisons.' } };
    const visit = (node: AzureStorageODataAst): AzureStorageODataValidationError | undefined => {
      if (node.kind === 'comparison') { const expected = options.declaredTypes?.[node.property]; return expected && expected !== node.value.t ? { code: 'TypeMismatch', message: `${node.property} expects ${expected}, not ${node.value.t}.` } : undefined; }
      return node.kind === 'not' ? visit(node.operand) : visit(node.left) ?? visit(node.right);
    };
    const error = visit(ast); return error ? { ok: false, error } : { ok: true, ast, comparisons };
  } catch (error) { return { ok: false, error: { code: 'InvalidFilter', message: error instanceof Error ? error.message : 'Invalid filter.' } }; }
}
